import type {
  AssetPurpose,
  ViewerAsset,
  ViewerContentType,
  ViewerDataSource,
  ViewerManifest,
} from '@genesisframeworks/viewer-plugin';
import { getApiBase } from '@services/api/apiConfig';

export interface PublicShareDataSourceOptions {
  shareId: string;
  fetchImpl?: typeof fetch;
}

async function responseError(response: Response, label: string): Promise<Error> {
  const body = await response.text().catch(() => '');
  if (response.status === 410) return new Error('This share was revoked by the owner.');
  return new Error(`${label}: HTTP ${response.status}${body ? ` ${body.slice(0, 200)}` : ''}`);
}

export class PublicShareDataSource implements ViewerDataSource {
  private readonly shareId: string;
  private readonly fetchImpl: typeof fetch;
  private readonly apiBase = getApiBase();

  constructor(options: PublicShareDataSourceOptions) {
    this.shareId = encodeURIComponent(options.shareId);
    this.fetchImpl = options.fetchImpl ?? fetch.bind(globalThis);
  }

  async loadManifest(options: {
    contentType: ViewerContentType;
    signal: AbortSignal;
  }): Promise<ViewerManifest | unknown> {
    const url = `${this.apiBase}/api/public/embed/${this.shareId}/manifest?type=${options.contentType}`;
    const response = await this.fetchImpl(url, {
      credentials: 'omit',
      signal: options.signal,
    });
    if (!response.ok) throw await responseError(response, 'Public manifest fetch failed');
    return response.json();
  }

  async loadAsset(
    ref: string,
    _options: { purpose: AssetPurpose; signal: AbortSignal },
  ): Promise<ViewerAsset> {
    // Public manifest URLs already encode the share capability. Returning the
    // URL preserves browser streaming/Range behavior for large PDFs.
    try {
      return { kind: 'url', url: new URL(ref, `${this.apiBase}/`).toString() };
    } catch {
      return { kind: 'url', url: ref };
    }
  }
}

export function publicDataSource(options: PublicShareDataSourceOptions): ViewerDataSource {
  return new PublicShareDataSource(options);
}
